import React, { useEffect, useMemo, useState } from "react";
import mainImg from "../image/main.png";
import mainImg2 from "../image/main2.png";
import starIcon from "../image/icon/star_icon.png";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import TokenPopup from "../../pages/Ads/components/detailComponents/TokenPopup";
import { fetchConciergeImagesByBusinessNumber } from "../../utils/conciergeImages";
import "../../styles/AdsHeader.css";

const AdsHeader = ({ imageList, data, tokenAll, onPrimaryClick }) => {
  const navigate = useNavigate();
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [storeSlides, setStoreSlides] = useState([]);

  const normalizedImageList = useMemo(() => {
    return Array.isArray(imageList) ? imageList : imageList ? [imageList] : [];
  }, [imageList]);

  const selectedAiImage = normalizedImageList[0] || null;
  const aiSlideImageSrc = selectedAiImage?.path || mainImg2;

  const storeBusinessNumber =
    data?.store_business_number || localStorage.getItem("store_business_number") || "";

  useEffect(() => {
    let alive = true;
    const sbn = String(storeBusinessNumber || "").trim();
    if (!sbn) {
      setStoreSlides([]);
      return;
    }

    (async () => {
      try {
        const conciergeData = await fetchConciergeImagesByBusinessNumber(sbn);
        if (!alive) return;

        const conciergeSlides = (conciergeData.images || [])
          .filter((image) => image?.public_url)
          .map((image) => ({
            key: `concierge-${image.file_id}`,
            src: image.public_url,
            attachSrc: `${process.env.REACT_APP_FASTAPI_ADS_URL}/concierge/images/${image.file_id}/file`,
            source: "concierge",
          }));

        if (conciergeSlides.length > 0) {
          setStoreSlides(conciergeSlides);
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/stores/by-business/${encodeURIComponent(
            sbn
          )}/siksin/banner-image`,
          { timeout: 8000 }
        );
        if (!alive) return;

        const url = typeof res?.data?.image_url === "string" ? res.data.image_url.trim() : "";
        if (url) {
          const proxiedUrl = `${process.env.REACT_APP_FASTAPI_ADS_URL}/siksin/proxy-image?url=${encodeURIComponent(
            url
          )}`;
          setStoreSlides([
            {
              key: "siksin-banner",
              src: url,
              attachSrc: proxiedUrl,
              source: "siksin",
            },
          ]);
          return;
        }

        setStoreSlides([]);
      } catch {
        if (!alive) return;
        setStoreSlides([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [storeBusinessNumber]);

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
  };

  const handleAutoAttachBanner = (slide) => {
    if ((tokenAll ?? 0) <= 0) {
      setShowTokenModal(true);
      return;
    }

    const url = String(slide?.attachSrc || "").trim();
    if (!url) {
      onPrimaryClick?.();
      return;
    }

    navigate("/manual/marketing/gallery", {
      state: {
        ...(data || {}),
        startStep: 2,
        imageUri: url,
        imagePreview: url,
        initialImageFromMenu: true,
      },
    });
  };

  const handleClickAIMarketing = () => {
    if ((tokenAll ?? 0) <= 0) {
      setShowTokenModal(true);
      return;
    }

    navigate("/manual/marketing/ai", {
      state: {
        ...(data || {}),
        image_list: normalizedImageList,
        headerSelectedPath: selectedAiImage?.path,
        headerSelectedPrompt: selectedAiImage?.prompt,
      },
    });
  };

  const resolvedStoreSlides =
    storeSlides.length > 0
      ? storeSlides
      : [{ key: "default-store-banner", src: mainImg, attachSrc: "", source: "default" }];

  return (
    <div className="relative w-full px-4">
      <div className="relative w-full overflow-hidden rounded-2xl">
        <div className="relative w-full aspect-square overflow-hidden rounded-2xl">
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={1}
            loop
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            className="mySwiper w-full h-full"
            touchStartPreventDefault={false}
            threshold={5}
          >
            {resolvedStoreSlides.map((slide) => (
              <SwiperSlide key={slide.key}>
                <div className="relative w-full h-full">
                  <img
                    src={slide.src}
                    alt="메인 배너"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  <div
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background:
                        "linear-gradient(0deg, rgba(0, 0, 0, 0.6) 14.19%, rgba(150, 150, 150, 0) 88.64%)",
                    }}
                  />

                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex flex-col items-start p-6">
                    <p className="text-white text-xl font-extrabold">
                      우리 매장 사진으로 바로 광고를 시작해 보세요
                    </p>
                    <p className="pb-3 pt-1 text-sm font-semibold text-white">
                      #{slide.source === "concierge" ? "컨시어지 등록 사진" : "매장 배너"} #이미지 바로 사용
                    </p>

                    <button
                      type="button"
                      onClick={() => handleAutoAttachBanner(slide)}
                      className="pointer-events-auto rounded-xl bg-[#7864F9] px-6 py-2 font-bold text-white"
                    >
                      바로 사용하기
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}

            <SwiperSlide key={`ai-${selectedAiImage?.design_id || "fallback"}`}>
              <div className="relative w-full h-full">
                <img
                  src={aiSlideImageSrc}
                  alt="AI 추천 배너"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background:
                      "linear-gradient(0deg, rgba(0, 0, 0, 0.6) 14.19%, rgba(150, 150, 150, 0) 88.64%)",
                  }}
                />

                <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex flex-col items-start p-6">
                  <p className="text-white text-xl font-extrabold">오늘의 AI 이미지 추천</p>
                  <p className="pb-3 pt-1 text-sm font-semibold text-white">#인스타 홍보 #자동 생성</p>

                  <button
                    type="button"
                    onClick={handleClickAIMarketing}
                    className="pointer-events-auto inline-flex items-center gap-2 rounded-xl border border-white/70 bg-black/60 px-4 py-2 font-extrabold text-white"
                  >
                    <span>AI 마케팅 시작</span>
                    <img src={starIcon} alt="" aria-hidden="true" className="w-4 h-4" draggable={false} />
                  </button>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <TokenPopup isOpen={showTokenModal} onClose={handleCloseTokenModal} />
    </div>
  );
};

export default AdsHeader;
